import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  User,
  LogOut,
  Menu,
  X,
  ChevronDown,
} from "lucide-react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import Images from "../../utils/Images";
import { clearUser } from "../../store/slices/authSlice";

const AuthenticatedNavbar = () => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  // Add null check for user object
  if (!user) {
    return null; // or return a loading spinner
  }

  const handleLogout = () => {
    dispatch(clearUser());
    toast.success("Logged out successfully");
    navigate("/");
  };

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/dashboard" className="flex gap-3 items-center">
              <img
                src={Images.logo_small}
                alt="Dealboxx"
                className="w-auto h-8"
              />
              <span className="text-xl font-bold bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] bg-clip-text text-transparent">
                Dealboxx
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden gap-6 items-center md:flex">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search deals..."
                className="pl-10 pr-4 py-2 w-48 lg:w-64 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6C6C] focus:border-transparent"
              />
            </div>

            {/* Profile Menu */}
            <div className="relative">
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex gap-3 items-center p-2 rounded-xl transition-colors hover:bg-gray-50"
              >
                <img
                  src={
                    user.avatar ||
                    "https://api.dicebear.com/7.x/avataaars/svg?seed=JohnDoe&backgroundColor=FF6C6C"
                  }
                  alt={
                    user.account_display_name.length > 15
                      ? user.account_display_name.slice(0, 15) + "..."
                      : user.account_display_name
                  }
                  className="object-cover w-8 h-8 rounded-full"
                />
                <div className="hidden text-left lg:block">
                  <p className="text-sm font-medium text-gray-900">
                    {user.account_display_name.length > 15
                      ? user.account_display_name.slice(0, 15) + "..."
                      : user.account_display_name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {user.email.length > 15
                      ? user.email.slice(0, 15) + "..."
                      : user.email}
                  </p>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              <AnimatePresence>
                {showProfileMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 py-2 mt-2 w-64 bg-white rounded-xl border border-gray-100 shadow-lg"
                  >
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="flex gap-3 items-center">
                        <img
                          src={
                            user.avatar ||
                            "https://api.dicebear.com/7.x/avataaars/svg?seed=JohnDoe&backgroundColor=FF6C6C"
                          }
                          alt={
                            user.account_display_name.length > 15
                              ? user.account_display_name.slice(0, 15) + "..."
                              : user.account_display_name
                          }
                          className="object-cover w-10 h-10 rounded-full"
                        />
                        <div>
                          <p className="font-medium text-gray-900">
                            {user.account_display_name.length > 15
                              ? user.account_display_name.slice(0, 15) + "..."
                              : user.account_display_name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {user.email.length > 15
                              ? user.email.slice(0, 15) + "..."
                              : user.email}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="py-2">
                      <Link
                        to="/profile"
                        className="flex gap-3 items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <User className="w-4 h-4" />
                        Profile
                      </Link>

                      <button
                        onClick={handleLogout}
                        className="flex gap-3 items-center px-4 py-2 w-full text-sm text-red-600 hover:bg-red-50"
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="p-2 text-gray-600 hover:text-gray-900"
            >
              {showMobileMenu ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {showMobileMenu && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="py-4 border-t border-gray-100 md:hidden"
            >
              <div className="space-y-4">
                {/* Mobile Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search deals..."
                    className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6C6C] focus:border-transparent"
                  />
                </div>

                {/* Mobile Profile */}
                <div className="flex gap-3 items-center p-3 bg-gray-50 rounded-xl">
                  <img
                    src={
                      user.avatar ||
                      "https://api.dicebear.com/7.x/avataaars/svg?seed=JohnDoe&backgroundColor=FF6C6C"
                    }
                    alt={
                      user.account_display_name.length > 15
                        ? user.account_display_name.slice(0, 15) + "..."
                        : user.account_display_name
                    }
                    className="object-cover w-10 h-10 rounded-full"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      {user.account_display_name.length > 15
                        ? user.account_display_name.slice(0, 15) + "..."
                        : user.account_display_name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {user.email.length > 15
                        ? user.email.slice(0, 15) + "..."
                        : user.email}
                    </p>
                  </div>
                </div>

                {/* Mobile Navigation Links */}
                <div className="space-y-2">
                  <Link
                    to="/profile"
                    className="flex gap-3 items-center px-3 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    <User className="w-4 h-4" />
                    Profile
                  </Link>

                  <button
                    onClick={handleLogout}
                    className="flex gap-3 items-center px-3 py-2 w-full text-sm text-red-600 rounded-lg hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default AuthenticatedNavbar;
